import { useCallback, useState } from 'react';

interface UseSectionEmptyTrackingResult {
    /** Report that the section at `index` resolved to empty (rendered null). */
    markEmpty: (index: number) => void;
    /** Highest index not yet reported empty, or -1 if every section is empty. */
    lastVisibleIndex: number;
}

/**
 * Tracks which sections in a mapped list have reported themselves empty (via
 * WillBeNullProvider/onEmpty) so the caller can place a trailing divider on
 * the section that's actually last *visible*, instead of the positionally
 * last one. Resets when `key` changes (e.g. the pack/feed identity), mirroring
 * the guideKeys reseed pattern in RecentContent.
 */
export function useSectionEmptyTracking(
    key: string,
    total: number
): UseSectionEmptyTrackingResult {
    const [emptyIndices, setEmptyIndices] = useState<ReadonlySet<number>>(
        () => new Set()
    );
    const [seededKey, setSeededKey] = useState(key);
    if (key !== seededKey) {
        setSeededKey(key);
        setEmptyIndices(new Set());
    }

    const markEmpty = useCallback((index: number) => {
        setEmptyIndices((prev) => {
            if (prev.has(index)) return prev;
            const next = new Set(prev);
            next.add(index);
            return next;
        });
    }, []);

    let lastVisibleIndex = -1;
    for (let i = total - 1; i >= 0; i--) {
        if (!emptyIndices.has(i)) {
            lastVisibleIndex = i;
            break;
        }
    }

    return { markEmpty, lastVisibleIndex };
}
